import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Plus, X, Check, ChartBar, Brain, ClipboardText, Trash } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

import { Spinner } from '@/components/ui';
import { ShareScreen } from '@/components/features/ShareScreen/ShareScreen';
import { useCreatePoll, useUpdatePoll, usePoll, useCreateForm } from '@/api';
import { sanitizeString } from '@/lib/sanitize';

import type { PollType, CreatePollPayload } from '@/types/poll';
import type { FieldType, CreateFormFieldPayload } from '@/types/form';

import styles from './CreatePoll.module.css';

const OPTION_COLORS = [
    'var(--option-color-1)',
    'var(--option-color-2)',
    'var(--option-color-3)',
    'var(--option-color-4)',
    'var(--option-color-5)',
    'var(--option-color-6)',
];

const FIELD_TYPE_OPTIONS: { label: string; value: FieldType }[] = [
    { label: 'Texto corto', value: 'short_text' },
    { label: 'Texto largo', value: 'long_text' },
    { label: 'Opcion unica', value: 'single_choice' },
    { label: 'Opcion multiple', value: 'multiple_choice' },
    { label: 'Desplegable', value: 'dropdown' },
    { label: 'Escala lineal', value: 'linear_scale' },
    { label: 'Fecha', value: 'date' },
    { label: 'Numero', value: 'number' },
    { label: 'Hora', value: 'time' },
];

interface OptionDraft {
    text: string;
    is_correct: boolean;
}

interface QuestionDraft {
    label: string;
    field_type: FieldType;
    is_required: boolean;
    config: Record<string, unknown>;
}

type CreationMode = 'poll' | 'quiz' | 'form';

const slideVariants = {
    enter: (dir: number) => ({
        x: dir > 0 ? 300 : -300,
        opacity: 0,
    }),
    center: {
        x: 0,
        opacity: 1,
    },
    exit: (dir: number) => ({
        x: dir > 0 ? -300 : 300,
        opacity: 0,
    }),
};

export function CreatePoll() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get('edit');
    const isEditMode = !!editId;

    const createPoll = useCreatePoll();
    const updatePoll = useUpdatePoll(editId || '');
    const createForm = useCreateForm();
    const { data: existingPoll, isLoading: isLoadingPoll } = usePoll(editId || '');

    const [step, setStep] = useState(1);
    const [direction, setDirection] = useState(1);
    const [mode, setMode] = useState<CreationMode>('poll');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [pollType] = useState<PollType>('single_choice');

    // Poll/Quiz options (single question)
    const [options, setOptions] = useState<OptionDraft[]>([
        { text: '', is_correct: false },
        { text: '', is_correct: false },
    ]);

    // Form questions (multi-question)
    const [questions, setQuestions] = useState<QuestionDraft[]>([
        { label: '', field_type: 'short_text', is_required: false, config: {} },
    ]);

    const [createdPoll, setCreatedPoll] = useState<{ slug: string; authorSlug: string } | null>(null);
    const [createdForm, setCreatedForm] = useState<{ slug: string; authorSlug: string } | null>(null);
    const questionRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        document.title = isEditMode ? 'Editar — Ratio' : 'Crear — Ratio';
    }, [isEditMode]);

    useEffect(() => {
        if (isEditMode && existingPoll) {
            setTitle(existingPoll.title);
            setDescription(existingPoll.description || '');
            setMode(existingPoll.is_quiz ? 'quiz' : 'poll');
            setOptions(
                existingPoll.options.map((o) => ({
                    text: o.text,
                    is_correct: o.is_correct ?? false,
                }))
            );
            setStep(2);
        }
    }, [isEditMode, existingPoll]);

    useEffect(() => {
        if (step === 2) {
            setTimeout(() => questionRef.current?.focus(), 300);
        }
    }, [step]);

    const goNext = () => { setDirection(1); setStep((s) => s + 1); };
    const goBack = () => { setDirection(-1); setStep((s) => s - 1); };

    const handleSelectMode = (m: CreationMode) => {
        setMode(m);
        goNext();
    };

    // --- Poll/Quiz option handlers ---
    const handleOptionChange = (index: number, text: string) => {
        setOptions((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], text };
            return next;
        });
    };

    const handleToggleCorrect = (index: number) => {
        setOptions((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], is_correct: !next[index].is_correct };
            return next;
        });
    };

    const handleAddOption = () => {
        if (options.length >= 6) { toast.error('Maximo 6 opciones'); return; }
        setOptions((prev) => [...prev, { text: '', is_correct: false }]);
    };

    const handleRemoveOption = (index: number) => {
        if (options.length <= 2) return;
        setOptions((prev) => prev.filter((_, i) => i !== index));
    };

    // --- Form question handlers ---
    const handleQuestionLabelChange = (index: number, label: string) => {
        setQuestions((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], label };
            return next;
        });
    };

    const handleQuestionTypeChange = (index: number, field_type: FieldType) => {
        setQuestions((prev) => {
            const next = [...prev];
            let config: Record<string, unknown> = {};
            if (['single_choice', 'multiple_choice', 'dropdown'].includes(field_type)) {
                config = { choices: ['', ''] };
            } else if (field_type === 'linear_scale') {
                config = { min: 1, max: 5 };
            }
            next[index] = { ...next[index], field_type, config };
            return next;
        });
    };

    const handleQuestionRequiredToggle = (index: number) => {
        setQuestions((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], is_required: !next[index].is_required };
            return next;
        });
    };

    const handleQuestionChoiceChange = (qIndex: number, cIndex: number, text: string) => {
        setQuestions((prev) => {
            const next = [...prev];
            const choices = [...((next[qIndex].config['choices'] as string[]) || [])];
            choices[cIndex] = text;
            next[qIndex] = { ...next[qIndex], config: { ...next[qIndex].config, choices } };
            return next;
        });
    };

    const handleAddQuestionChoice = (qIndex: number) => {
        setQuestions((prev) => {
            const next = [...prev];
            const choices = [...((next[qIndex].config['choices'] as string[]) || [])];
            if (choices.length >= 10) { toast.error('Maximo 10 opciones'); return prev; }
            choices.push('');
            next[qIndex] = { ...next[qIndex], config: { ...next[qIndex].config, choices } };
            return next;
        });
    };

    const handleRemoveQuestionChoice = (qIndex: number, cIndex: number) => {
        setQuestions((prev) => {
            const next = [...prev];
            const choices = ((next[qIndex].config['choices'] as string[]) || []).filter((_, i) => i !== cIndex);
            next[qIndex] = { ...next[qIndex], config: { ...next[qIndex].config, choices } };
            return next;
        });
    };

    const handleAddQuestion = () => {
        if (questions.length >= 50) { toast.error('Maximo 50 preguntas'); return; }
        setQuestions((prev) => [...prev, { label: '', field_type: 'short_text', is_required: false, config: {} }]);
    };

    const handleRemoveQuestion = (index: number) => {
        if (questions.length <= 1) return;
        setQuestions((prev) => prev.filter((_, i) => i !== index));
    };

    // --- Validation ---
    const validateStep2 = () => {
        if (!title.trim()) { toast.error('Escribe tu pregunta'); return false; }
        return true;
    };

    const validateStep2Form = () => {
        if (!title.trim()) { toast.error('Escribe el titulo del formulario'); return false; }
        return true;
    };

    const validateStep3 = () => {
        const validOptions = options.filter((o) => o.text.trim());
        if (validOptions.length < 2) { toast.error('Agrega al menos 2 opciones'); return false; }
        if (mode === 'quiz' && !validOptions.some((o) => o.is_correct)) {
            toast.error('Marca al menos una respuesta correcta');
            return false;
        }
        return true;
    };

    const validateStep3Form = () => {
        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            if (!q.label.trim()) {
                toast.error(`La pregunta ${i + 1} no tiene texto`);
                return false;
            }
            if (['single_choice', 'multiple_choice', 'dropdown'].includes(q.field_type)) {
                const choices = (q.config['choices'] as string[] | undefined) ?? [];
                if (choices.filter((c) => c.trim()).length < 2) {
                    toast.error(`La pregunta "${q.label}" necesita al menos 2 opciones`);
                    return false;
                }
            }
        }
        return true;
    };

    // --- Submit ---
    const handleSubmitPoll = () => {
        if (!validateStep3()) return;
        const validOptions = options.filter((o) => o.text.trim());
        const payload: CreatePollPayload = {
            title: sanitizeString(title.trim()),
            description: description.trim() ? sanitizeString(description.trim()) : undefined,
            poll_type: pollType,
            is_quiz: mode === 'quiz',
            options_data: validOptions.map((o, i) => ({
                text: sanitizeString(o.text.trim()),
                order: i,
                is_correct: mode === 'quiz' ? o.is_correct : undefined,
            })),
        };

        if (isEditMode && editId) {
            updatePoll.mutate(payload, {
                onSuccess: (poll) => navigate(`/${poll.author.slug}/${poll.slug}`),
            });
        } else {
            createPoll.mutate(payload, {
                onSuccess: (poll) => {
                    setCreatedPoll({ slug: poll.slug, authorSlug: poll.author.slug });
                    goNext();
                },
            });
        }
    };

    const buildFormPayload = (visibility: 'public' | 'unlisted' | 'private') => {
        const fieldsData: CreateFormFieldPayload[] = questions.map((q, i) => ({
            label: sanitizeString(q.label.trim()),
            field_type: q.field_type,
            is_required: q.is_required,
            order: i,
            config: q.config,
            description: '',
            points: 0,
            correct_answer: {},
            shuffle_choices: false,
        }));
        return {
            title: sanitizeString(title.trim()),
            description: description.trim() ? sanitizeString(description.trim()) : undefined,
            visibility,
            fields_data: fieldsData,
        };
    };

    const handleSubmitForm = () => {
        if (!validateStep3Form()) return;
        createForm.mutate(buildFormPayload('public'), {
            onSuccess: (form) => {
                toast.success('Formulario publicado!');
                setCreatedForm({ slug: form.slug, authorSlug: form.author.slug });
                setStep(4);
            },
            onError: () => {
                toast.error('No se pudo crear el formulario. Intenta de nuevo.');
            },
        });
    };

    const handleSavePrivate = () => {
        if (!validateStep3Form()) return;
        createForm.mutate(buildFormPayload('unlisted'), {
            onSuccess: (form) => {
                toast.success('Formulario guardado! Comparte el enlace para recibir respuestas.');
                setCreatedForm({ slug: form.slug, authorSlug: form.author.slug });
                setStep(4);
            },
            onError: () => {
                toast.error('No se pudo guardar el formulario. Intenta de nuevo.');
            },
        });
    };

    const isPending = createPoll.isPending || updatePoll.isPending || createForm.isPending;

    if (isEditMode && isLoadingPoll) {
        return (
            <div className={styles.page}>
                <div className={styles.loadingContainer}>
                    <Spinner size="lg" />
                </div>
            </div>
        );
    }

    // Step 4: Share screen
    if (step === 4 && createdPoll) {
        return (
            <div className={styles.page}>
                <ShareScreen
                    userSlug={createdPoll.authorSlug}
                    pollSlug={createdPoll.slug}
                    pollTitle={title}
                    onViewPoll={() => navigate(`/${createdPoll.authorSlug}/${createdPoll.slug}`)}
                />
            </div>
        );
    }

    if (step === 4 && createdForm) {
        return (
            <div className={styles.page}>
                <ShareScreen
                    userSlug={createdForm.authorSlug}
                    pollSlug={createdForm.slug}
                    pollTitle={title}
                    onViewPoll={() => navigate(`/${createdForm.authorSlug}/f/${createdForm.slug}`)}
                />
            </div>
        );
    }

    return (
        <div className={styles.page}>
            {step > 1 && (
                <button className={styles.backBtn} onClick={goBack} aria-label="Volver">
                    <ArrowLeft size={18} />
                    Volver
                </button>
            )}
            {step === 1 && (
                <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Volver">
                    <ArrowLeft size={18} />
                    Volver
                </button>
            )}

            {/* Progress */}
            <div className={styles.progress}>
                {[1, 2, 3].map((s) => (
                    <div key={s} className={`${styles.dot} ${step >= s ? styles.dotActive : ''}`} />
                ))}
            </div>

            <AnimatePresence mode="wait" custom={direction}>
                {/* Step 1: Choose mode */}
                {step === 1 && (
                    <motion.div
                        key="step1"
                        className={styles.stepContainer}
                        custom={direction}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                    >
                        <h1 className={styles.stepTitle}>¿Que quieres crear?</h1>
                        <div className={styles.typeCards}>
                            <motion.button
                                className={styles.typeCard}
                                onClick={() => handleSelectMode('poll')}
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                            >
                                <ChartBar size={32} weight="duotone" className={styles.typeIcon} />
                                <span className={styles.typeTitle}>Encuesta</span>
                                <span className={styles.typeSubtitle}>Una pregunta con opciones</span>
                            </motion.button>

                            <motion.button
                                className={styles.typeCard}
                                onClick={() => handleSelectMode('quiz')}
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                            >
                                <Brain size={32} weight="duotone" className={styles.typeIcon} />
                                <span className={styles.typeTitle}>Trivia</span>
                                <span className={styles.typeSubtitle}>Pregunta con respuesta correcta</span>
                            </motion.button>

                            <motion.button
                                className={styles.typeCard}
                                onClick={() => handleSelectMode('form')}
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                            >
                                <ClipboardText size={32} weight="duotone" className={styles.typeIcon} />
                                <span className={styles.typeTitle}>Formulario</span>
                                <span className={styles.typeSubtitle}>Multiples preguntas, distintos tipos</span>
                            </motion.button>
                        </div>
                    </motion.div>
                )}

                {/* Step 2: Title (same for all modes) */}
                {step === 2 && (
                    <motion.div
                        key="step2"
                        className={styles.stepContainer}
                        custom={direction}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                    >
                        <h1 className={styles.stepTitle}>
                            {mode === 'form' ? 'Titulo del formulario' : 'Tu pregunta'}
                        </h1>
                        <div className={styles.questionFields}>
                            <input
                                ref={questionRef}
                                className={styles.questionInput}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder={mode === 'form' ? '¿Como se llama tu formulario?' : '¿Cual es tu pregunta?'}
                                maxLength={200}
                            />
                            <input
                                className={styles.descriptionInput}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Agrega contexto (opcional)"
                                maxLength={500}
                            />
                        </div>
                        <button
                            className={styles.nextBtn}
                            onClick={() => {
                                if (mode === 'form' ? validateStep2Form() : validateStep2()) goNext();
                            }}
                        >
                            Siguiente
                            <ArrowRight size={18} />
                        </button>
                    </motion.div>
                )}

                {/* Step 3: Options (poll/quiz) OR Questions (form) */}
                {step === 3 && mode !== 'form' && (
                    <motion.div
                        key="step3-poll"
                        className={styles.stepContainer}
                        custom={direction}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                    >
                        <h1 className={styles.stepTitle}>Opciones</h1>
                        <div className={styles.optionsList}>
                            {options.map((option, index) => (
                                <div
                                    key={index}
                                    className={styles.optionBlock}
                                    style={{ backgroundColor: OPTION_COLORS[index % OPTION_COLORS.length] } as React.CSSProperties}
                                >
                                    <input
                                        className={styles.optionInput}
                                        value={option.text}
                                        onChange={(e) => handleOptionChange(index, e.target.value)}
                                        placeholder={`Opcion ${index + 1}`}
                                        maxLength={200}
                                    />
                                    <div className={styles.optionActions}>
                                        {mode === 'quiz' && (
                                            <button
                                                type="button"
                                                className={`${styles.correctToggle} ${option.is_correct ? styles.correctActive : ''}`}
                                                onClick={() => handleToggleCorrect(index)}
                                                aria-label={option.is_correct ? 'Desmarcar como correcta' : 'Marcar como correcta'}
                                            >
                                                <Check size={14} weight="bold" />
                                                {option.is_correct ? 'Correcta' : ''}
                                            </button>
                                        )}
                                        {options.length > 2 && (
                                            <button
                                                type="button"
                                                className={styles.removeBtn}
                                                onClick={() => handleRemoveOption(index)}
                                                aria-label="Eliminar opcion"
                                            >
                                                <X size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {options.length < 6 && (
                            <button type="button" className={styles.addOptionBtn} onClick={handleAddOption}>
                                <Plus size={16} weight="bold" />
                                Agregar opcion
                            </button>
                        )}

                        <button
                            className={styles.nextBtn}
                            onClick={handleSubmitPoll}
                            disabled={isPending}
                        >
                            <span style={{ visibility: isPending ? 'hidden' : 'visible' }}>{isEditMode ? 'Guardar cambios' : 'Crear'}</span>
                            {isPending && <Spinner size="sm" variant="inverse" className={styles.btnSpinner} />}
                        </button>
                    </motion.div>
                )}

                {/* Step 3: Questions (form mode) */}
                {step === 3 && mode === 'form' && (
                    <motion.div
                        key="step3-form"
                        className={styles.stepContainer}
                        custom={direction}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                    >
                        <h1 className={styles.stepTitle}>Preguntas</h1>
                        <div className={styles.questionsList}>
                            {questions.map((q, qi) => (
                                <div key={qi} className={styles.questionCard}>
                                    <div className={styles.questionCardHeader}>
                                        <span className={styles.questionNumber}>{qi + 1}</span>
                                        <select
                                            className={styles.questionTypeSelect}
                                            value={q.field_type}
                                            onChange={(e) => handleQuestionTypeChange(qi, e.target.value as FieldType)}
                                        >
                                            {FIELD_TYPE_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <label className={styles.requiredLabel}>
                                            <input
                                                type="checkbox"
                                                checked={q.is_required}
                                                onChange={() => handleQuestionRequiredToggle(qi)}
                                            />
                                            <span>Obligatorio</span>
                                        </label>
                                        {questions.length > 1 && (
                                            <button
                                                type="button"
                                                className={styles.questionDeleteBtn}
                                                onClick={() => handleRemoveQuestion(qi)}
                                                aria-label="Eliminar pregunta"
                                            >
                                                <Trash size={16} />
                                            </button>
                                        )}
                                    </div>

                                    <input
                                        className={styles.questionLabelInput}
                                        value={q.label}
                                        onChange={(e) => handleQuestionLabelChange(qi, e.target.value)}
                                        placeholder={`Pregunta ${qi + 1}`}
                                        maxLength={300}
                                    />

                                    {/* Choices editor for choice-type questions */}
                                    {['single_choice', 'multiple_choice', 'dropdown'].includes(q.field_type) && (
                                        <div className={styles.questionChoices}>
                                            {((q.config['choices'] as string[]) || []).map((choice, ci) => (
                                                <div key={ci} className={styles.questionChoiceRow}>
                                                    <span
                                                        className={styles.questionChoiceDot}
                                                        style={{ backgroundColor: OPTION_COLORS[ci % OPTION_COLORS.length] } as React.CSSProperties}
                                                    />
                                                    <input
                                                        className={styles.questionChoiceInput}
                                                        value={choice}
                                                        onChange={(e) => handleQuestionChoiceChange(qi, ci, e.target.value)}
                                                        placeholder={`Opcion ${ci + 1}`}
                                                        maxLength={200}
                                                    />
                                                    {((q.config['choices'] as string[]) || []).length > 2 && (
                                                        <button
                                                            type="button"
                                                            className={styles.questionChoiceRemove}
                                                            onClick={() => handleRemoveQuestionChoice(qi, ci)}
                                                            aria-label="Eliminar opcion"
                                                        >
                                                            <X size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                className={styles.questionAddChoice}
                                                onClick={() => handleAddQuestionChoice(qi)}
                                            >
                                                <Plus size={12} />
                                                Opcion
                                            </button>
                                        </div>
                                    )}

                                    {/* Scale preview */}
                                    {q.field_type === 'linear_scale' && (
                                        <div className={styles.scalePreview}>
                                            {Array.from({ length: 5 }, (_, i) => (
                                                <span key={i} className={styles.scaleDot}>{i + 1}</span>
                                            ))}
                                        </div>
                                    )}

                                    {/* Type hint for simple fields */}
                                    {['short_text', 'long_text', 'date', 'number', 'time'].includes(q.field_type) && (
                                        <div className={styles.fieldHint}>
                                            {q.field_type === 'short_text' && 'Respuesta de texto corto'}
                                            {q.field_type === 'long_text' && 'Respuesta de texto largo'}
                                            {q.field_type === 'date' && 'Selector de fecha'}
                                            {q.field_type === 'number' && 'Respuesta numerica'}
                                            {q.field_type === 'time' && 'Selector de hora'}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {questions.length < 50 && (
                            <button type="button" className={styles.addOptionBtn} onClick={handleAddQuestion}>
                                <Plus size={16} weight="bold" />
                                Agregar pregunta
                            </button>
                        )}

                        <div className={styles.formActions}>
                            <button
                                className={styles.nextBtn}
                                onClick={handleSubmitForm}
                                disabled={isPending}
                            >
                                <span style={{ visibility: isPending ? 'hidden' : 'visible' }}>Publicar</span>
                                {isPending && <Spinner size="sm" variant="inverse" className={styles.btnSpinner} />}
                            </button>
                            <button
                                className={styles.savePrivateBtn}
                                onClick={handleSavePrivate}
                                disabled={isPending}
                                type="button"
                            >
                                Guardar como privado
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
